import React, { Component, useState } from "react";
import Container from "../../components/Container";
import "./InroadsToTechnology.css";

const overview = [
  "In today’s environment, organizations prioritize customer, digital, and employee experiences. Achieving these outcomes depends on effective program delivery.",
  "Strong program management, clear governance, and disciplined execution provide the foundation for technology initiatives to deliver meaningful, lasting value.",
  "Inroads to Technology was developed through this understanding. We partner with organizations to strengthen delivery through thoughtful planning, collaborative implementation, and continuous improvement—supporting alignment, effective execution, and measurable impact.",
];

const services = [
  {
    title: "Project Management Consulting",
    text: "From focused initiatives to mid-scale program delivery, we provide experienced oversight and strategic guidance across Agile, hybrid, and structured methodologies. Our approach ensures stability, alignment, and consistent progress toward successful outcomes.",
  },
  {
    title: "Business Analysis & Process Optimization",
    text: "We bring clarity to complexity. Through structured analysis, process mapping, and collaborative workshops, we streamline workflows and strengthen operational efficiency with practical, well-defined solutions.",
  },
  {
    title: "Technology Solutions",
    text: "We design and deliver tailored technology solutions, including CRM platforms, web-based applications, and internal systems. When appropriate, we collaborate with trusted partners to ensure each solution is thoughtfully aligned and fit for purpose.",
  },
  {
    title: "Customer Experience (CX) Strategy",
    text: "We support the design of intuitive, end-to-end customer experiences. By developing clear journey frameworks, feedback mechanisms, and responsive processes, we help organizations strengthen engagement and improve outcomes.",
  },
  {
    title: "Specialized Healthcare Solutions",
    text: "We develop tailored tools to support healthcare providers, including intake systems, patient tracking, and post-care engagement solutions. Our approach emphasizes accessibility, continuity of care, and practical implementation.",
  },
  {
    title: "Systems Evaluation & Data Governance",
    text: "We assess system performance, usability, and risk to ensure technology environments remain stable and effective. In parallel, we establish data governance frameworks that promote accuracy, compliance, and informed decision-making.",
  },
  {
    title: "Legacy System Transition Strategies",
    text: "Helping organizations move smoothly from outdated platforms to future-ready tech.",
  },
  {
    title: "CRM & Digital Solution Delivery",
    text: "Guiding implementations of customer-facing and internal platforms that transform operations.",
  },
];

const deliveryApproach = [
  {
    title: "Authenticity over Perfection",
    text: "We value honest, transparent communication over complexity. Clear conversations and meaningful progress.",
  },
  {
    title: "Driven by Momentum",
    text: "We believe in moving forward with intention. Learning quickly, and adapting as needed, we maintain steady progress and deliver results efficiently.",
  },
  {
    title: "People-Centered Approach",
    text: "Relationships are at the core of our work, recognizing that strong partnerships drive better outcomes. We prioritize collaboration.",
  },
  {
    title: "Commitment to Delivery Excellence",
    text: "We take pride in delivering high-quality work that is timely, thoughtful, and aligned with your goals. Execution is not just a process—it is a responsibility we take seriously.",
  },
  {
    title: "Positive Energy, Purposeful Work",
    text: "We believe that a positive and engaged environment leads to stronger performance and meaningful results.",
  },
  {
    title: "Accountability and Ownership",
    text: "We hold ourselves accountable for our work and our outcomes. With a strong sense of responsibility, we stand behind what we deliver and strive for continuous improvement.",
  },
  {
    title: "Progress That Builds Impact",
    text: "We recognize that meaningful results are built over time. Consistent, incremental progress creates lasting impact and drives long-term success.",
  },
];

const team = [
  {
    name: "Syed M. Umer",
    image: "./img/Syed-Umer.jpg",
    bio: [
      "Syed M. Umer brings a thoughtful balance of technical expertise and people-centered leadership to Inroads to Technology. He holds a Bachelor’s degree in Computer Science and a Master’s in Information Technology, along with certifications in project management and Agile delivery.",
      "His experience includes leading complex digital initiatives and supporting organizations in strengthening program delivery, improving collaboration, and achieving meaningful, sustainable outcomes. Syed’s approach is rooted in clarity, structure, and a commitment to helping teams work effectively within supportive and well-aligned environments.",
      "He is known for fostering strong relationships and creating spaces where individuals feel respected, accountable, and empowered to contribute. His work focuses on building practical solutions that support long-term growth and organizational stability.",
      "Beyond his professional role, Syed values creativity and balance, with a personal interest in music that reflects his belief in bringing both perspective and purpose into his work.",
    ],
  },
  {
    name: "S. Lukmaan Sattar",
    image: "./img/lukka.jpg",
    bio: [
      "S. Lukmaan Sattar brings a strong analytical foundation and a thoughtful, data-informed perspective to Inroads to Technology. He holds a Bachelor of Science in Business Administration (BSBA) and a Master of Science in Analytics from Creighton University, supporting his work in applying data to real-world decision-making.",
      "His expertise includes SQL, advanced data modeling, and visualization tools such as Power BI and Tableau, enabling organizations to translate complex data into meaningful insight.",
      "Lukmaan’s work focuses on helping teams better understand information, identify patterns, and make informed decisions that support operational effectiveness and long-term growth. He approaches data with precision and care, ensuring that insights are both accurate and practical in application.",
    ],
  },
];

function Section({ title, children }) {
  // Collapse/expand only takes effect on mobile (see CSS media query);
  // on desktop the body is always shown and the toggle icon is hidden.
  const [open, setOpen] = useState(false);
  return (
    <div className={open ? "tech-section is-open" : "tech-section"}>
      <button
        type="button"
        className="tech-section-header"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span className="tech-section-toggle" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      <div className="tech-section-body">{children}</div>
    </div>
  );
}

class InroadsToTechnology extends Component {
  render() {
    return (
      <Container backgroundColor="#ffffff">
        <div className="inroads-to-technology">
          <div className="tech-tagline">
            A boutique approach to digital delivery—grounded in purpose and
            driven by excellence.
          </div>

          <div className="tech-overview">
            {overview.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="tech-sections">
            <Section title="Mission and Vision">
              <h3>Mission</h3>
              <p>
                We are committed to delivering digital solutions that are
                thoughtful, effective, and results-driven.
              </p>
              <h3>Vision</h3>
              <p>
                Our Vision is to support organizations in executing meaningful
                projects with both discipline and intention. We go beyond simply
                meeting requirements—we help drive progress, strengthen teams,
                and create delivery processes that are both efficient and
                impactful.
              </p>
              <p>
                From initial concept through execution, we combine strong human
                connection with proven delivery practices, ensuring each step is
                purposeful and aligned with your goals.
              </p>
              <p>
                The result is a seamless and reliable experience—built on
                clarity, consistency, and quality.
              </p>
              <p>
                Let’s build it the right way: together we can{" "}
                <strong>INCLUDE</strong>, <strong>IMPROVE</strong> and{" "}
                <strong>INSPIRE</strong>.
              </p>
            </Section>

            <Section title="Services">
              <div className="tech-box-grid">
                {services.map((item, index) => (
                  <div className="tech-box" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Our Delivery Approach">
              <div className="tech-box-grid">
                {deliveryApproach.map((item, index) => (
                  <div className="tech-box" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Our Team">
              <div className="tech-team">
                {team.map((member, index) => (
                  <div className="tech-team-member" key={index}>
                    <div className="tech-team-photo">
                      <img src={member.image} alt={member.name} />
                    </div>
                    <div className="tech-team-bio">
                      <h3>{member.name}</h3>
                      {member.bio.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </Container>
    );
  }
}

export default InroadsToTechnology;
